import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  clockInputErrorMessage,
  formatClock12FromParts,
  normalizeClock12,
  normalizeTypedClock12,
  parseClockParts
} from '../lib/proposalAcceptance';

const FONT = "'Neue Haas Unica', 'Inter', sans-serif";
const INK = '#2C2C2C';
const MUTED = '#8a8378';
const RULE = '#d8d0c4';
const PAGE = '#fafaf8';
const SELECTED = '#f4f1ea';
const COMMON_MINUTES = [0, 15, 30, 45];
const DEFAULT_PARTS = { hour: 5, minute: 0, period: 'PM' };

export function clock12ToInput24(clock12) {
  const parts = parseClockParts(clock12);
  if (!parts) return '';
  const hours = parts.period === 'PM' ? (parts.hour % 12) + 12 : (parts.hour % 12);
  return `${String(hours).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

export function toTimeOutput(clock12, outputFormat = 'clock12') {
  const normalized = normalizeClock12(clock12);
  if (!normalized) return '';
  return outputFormat === 'input24' ? clock12ToInput24(normalized) : normalized;
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke={MUTED} strokeWidth="1.4" />
      <path d="M12 7.5V12l3.1 2" stroke={MUTED} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function compactFieldStyle(focused) {
  return {
    width: 44,
    height: 36,
    boxSizing: 'border-box',
    textAlign: 'center',
    border: `1px solid ${focused ? '#2C2C2C' : RULE}`,
    borderRadius: 4,
    backgroundColor: '#fff',
    color: INK,
    fontSize: 16,
    fontFamily: FONT,
    fontWeight: 400,
    outline: 'none',
    padding: 0,
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'textfield'
  };
}

export default function MaykerTimeInput({
  id,
  value,
  onChange,
  outputFormat = 'clock12',
  placeholder = '5:00 PM',
  required = false,
  disabled = false,
  style,
  'aria-label': ariaLabel,
  onRegisterValidator
}) {
  const committed = normalizeClock12(value);
  const [draft, setDraft] = useState(committed);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState(null);
  const [hourDraft, setHourDraft] = useState('');
  const [minuteDraft, setMinuteDraft] = useState('');
  const [focusedPart, setFocusedPart] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const hourRef = useRef(null);
  const draftRef = useRef(draft);
  const committedRef = useRef(committed);
  const onChangeRef = useRef(onChange);
  const outputFormatRef = useRef(outputFormat);
  const reactId = useId();
  const fieldId = id || `mayker-time-${reactId}`;
  const popoverId = `${fieldId}-popover`;

  draftRef.current = draft;
  committedRef.current = committed;
  onChangeRef.current = onChange;
  outputFormatRef.current = outputFormat;

  const emit = (clock12) => {
    onChangeRef.current(toTimeOutput(clock12, outputFormatRef.current));
  };

  const applyDraft = (raw) => {
    const trimmed = String(raw || '').trim();
    if (!trimmed) {
      emit('');
      setDraft('');
      setError('');
      return true;
    }
    const normalized = normalizeTypedClock12(trimmed, committedRef.current);
    if (normalized) {
      emit(normalized);
      setDraft(normalized);
      setError('');
      return true;
    }
    setError(clockInputErrorMessage(trimmed, committedRef.current));
    return false;
  };

  useEffect(() => {
    if (inputRef.current && document.activeElement === inputRef.current) return;
    setDraft(committed);
    setError('');
  }, [committed]);

  useEffect(() => {
    if (!onRegisterValidator) return undefined;
    return onRegisterValidator(fieldId, () => applyDraft(draftRef.current));
  }, [fieldId, onRegisterValidator]);

  const syncPopoverDrafts = (parts) => {
    const next = parts || parseClockParts(committedRef.current) || DEFAULT_PARTS;
    setHourDraft(String(next.hour));
    setMinuteDraft(String(next.minute).padStart(2, '0'));
  };

  const openPopover = () => {
    if (disabled) return;
    syncPopoverDrafts(parseClockParts(committed) || DEFAULT_PARTS);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const width = 248;
      const height = 118;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < height + 12 && rect.top > spaceBelow;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      setPickerPos({
        top: openAbove ? Math.max(8, rect.top - 6 - height) : rect.bottom + 6,
        left,
        width
      });
    };
    place();
    const onDoc = (event) => {
      if (wrapRef.current && wrapRef.current.contains(event.target)) return;
      if (popoverRef.current && popoverRef.current.contains(event.target)) return;
      setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        if (inputRef.current) inputRef.current.focus();
      }
    };
    window.addEventListener('resize', place);
    document.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    const focusTimer = window.setTimeout(() => {
      if (hourRef.current) hourRef.current.select();
    }, 0);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('resize', place);
      document.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const popoverParts = parseClockParts(committed) || DEFAULT_PARTS;

  const commitParts = (nextParts) => {
    const clock = formatClock12FromParts(nextParts);
    emit(clock);
    setDraft(clock);
    setError('');
    syncPopoverDrafts(nextParts);
  };

  const commitHour = (raw) => {
    const parsed = parseInt(String(raw).replace(/\D/g, ''), 10);
    if (!parsed || parsed < 1 || parsed > 12) {
      syncPopoverDrafts(popoverParts);
      return;
    }
    commitParts({ ...popoverParts, hour: parsed });
  };

  const commitMinute = (raw) => {
    const digits = String(raw).replace(/\D/g, '');
    if (digits === '') {
      syncPopoverDrafts(popoverParts);
      return;
    }
    const parsed = parseInt(digits, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 59) {
      syncPopoverDrafts(popoverParts);
      return;
    }
    commitParts({ ...popoverParts, minute: parsed });
  };

  const nudgeHour = (delta) => {
    const current = popoverParts.hour || 12;
    const next = ((current - 1 + delta) % 12 + 12) % 12 + 1;
    commitParts({ ...popoverParts, hour: next });
  };

  const nudgeMinute = (delta) => {
    const current = popoverParts.minute || 0;
    const next = (current + delta + 60) % 60;
    commitParts({ ...popoverParts, minute: next });
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 36px 11px 12px',
    minHeight: 44,
    border: `1px solid ${error ? '#d4b8b0' : RULE}`,
    borderRadius: 6,
    fontSize: 14,
    fontFamily: FONT,
    color: draft ? INK : MUTED,
    backgroundColor: '#fff',
    outline: 'none',
    ...style,
    paddingRight: 36
  };

  return (
    <div>
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          id={fieldId}
          className="mayker-field"
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={draft}
          required={required}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          aria-haspopup="dialog"
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            setError('');
            const normalized = normalizeTypedClock12(next, committedRef.current);
            if (normalized) emit(normalized);
          }}
          onBlur={(e) => {
            if (wrapRef.current && wrapRef.current.contains(e.relatedTarget)) return;
            if (popoverRef.current && popoverRef.current.contains(e.relatedTarget)) return;
            applyDraft(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              applyDraft(draft);
              setOpen(false);
            }
            if (e.key === 'ArrowDown' && !open) {
              e.preventDefault();
              openPopover();
            }
          }}
          style={inputStyle}
        />
        <button
          type="button"
          aria-label="Choose time"
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          disabled={disabled}
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (open) setOpen(false);
            else openPopover();
          }}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 24,
            height: 24,
            padding: 0,
            border: 0,
            background: 'transparent',
            cursor: disabled ? 'default' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ClockIcon />
        </button>
      </div>
      {open && pickerPos && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="dialog"
            aria-label="Choose time"
            style={{
              position: 'fixed',
              zIndex: 13000,
              top: pickerPos.top,
              left: pickerPos.left,
              width: pickerPos.width,
              boxSizing: 'border-box',
              padding: '12px 12px 10px',
              backgroundColor: '#fff',
              border: `1px solid ${RULE}`,
              borderRadius: 4,
              boxShadow: '0 8px 20px rgba(44, 44, 44, 0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                ref={hourRef}
                aria-label="Hour"
                inputMode="numeric"
                autoComplete="off"
                value={hourDraft}
                onFocus={(e) => {
                  setFocusedPart('hour');
                  e.target.select();
                }}
                onBlur={(e) => {
                  setFocusedPart((part) => (part === 'hour' ? '' : part));
                  commitHour(e.target.value);
                }}
                onChange={(e) => setHourDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    nudgeHour(1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    nudgeHour(-1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    commitHour(e.target.value);
                    setOpen(false);
                    if (inputRef.current) inputRef.current.focus();
                  }
                }}
                style={compactFieldStyle(focusedPart === 'hour')}
              />
              <span aria-hidden="true" style={{ color: MUTED, fontFamily: FONT, fontSize: 16 }}>:</span>
              <input
                aria-label="Minutes"
                inputMode="numeric"
                autoComplete="off"
                value={minuteDraft}
                onFocus={(e) => {
                  setFocusedPart('minute');
                  e.target.select();
                }}
                onBlur={(e) => {
                  setFocusedPart((part) => (part === 'minute' ? '' : part));
                  commitMinute(e.target.value);
                }}
                onChange={(e) => setMinuteDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    nudgeMinute(1);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    nudgeMinute(-1);
                  } else if (e.key === 'Enter') {
                    e.preventDefault();
                    commitMinute(e.target.value);
                    setOpen(false);
                    if (inputRef.current) inputRef.current.focus();
                  }
                }}
                style={compactFieldStyle(focusedPart === 'minute')}
              />
              <div role="group" aria-label="AM or PM" style={{ display: 'inline-flex', marginLeft: 4, border: `1px solid ${RULE}`, borderRadius: 4, overflow: 'hidden' }}>
                {['AM', 'PM'].map((period) => {
                  const selected = popoverParts.period === period;
                  return (
                    <button
                      key={period}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => commitParts({ ...popoverParts, period })}
                      style={{
                        width: 40,
                        height: 36,
                        border: 0,
                        backgroundColor: selected ? SELECTED : '#fff',
                        color: INK,
                        fontSize: 12,
                        fontFamily: FONT,
                        letterSpacing: '0.04em',
                        cursor: 'pointer'
                      }}
                    >
                      {period}
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {COMMON_MINUTES.map((minute) => {
                const selected = popoverParts.minute === minute;
                return (
                  <button
                    key={minute}
                    type="button"
                    onClick={() => commitParts({ ...popoverParts, minute })}
                    style={{
                      flex: 1,
                      height: 28,
                      border: `1px solid ${selected ? '#c9c1b4' : RULE}`,
                      borderRadius: 4,
                      backgroundColor: selected ? SELECTED : PAGE,
                      color: INK,
                      fontSize: 12,
                      fontFamily: FONT,
                      cursor: 'pointer'
                    }}
                  >
                    {String(minute).padStart(2, '0')}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
        : null}
      {error ? (
        <div style={{ marginTop: 6, fontSize: 12, color: '#7a5348', fontFamily: FONT }}>{error}</div>
      ) : null}
    </div>
  );
}
